import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import {
    WarehouseClient,
    WarehouseInvoiceService,
    WarehousePendingService
} from 'src/app/services/warehouse-invoice.service';
import { WarehouseService } from 'src/app/services/warehouse.service';

/** Un servicio ya prestado, convertido en concepto editable de la factura. */
interface ServiceLine {
    serviceId: number;
    folio: string;
    warehouse: string;
    concept: string;
    description: string;
    quantity: number;
    unitKey: string;
    unitPrice: number;
    productServiceKey: string;
    taxObjectKey: string;
}

/** Una línea de "Insumos utilizados": qué se usó, cuánto y a qué precio. */
interface SupplyLine {
    supply: string | null;
    quantity: number | null;
    unitPrice: number | null;
    open: boolean;
}

/** Insumo del catálogo. `cost` sólo lo traen los dados de alta en la pantalla. */
interface Supply {
    name: string;
    cost: number | null;
}

/** Comprobante de pago adjunto. */
interface Receipt {
    name: string;
    isPdf: boolean;
    /** Object URL para la miniatura; se libera al quitar el archivo. */
    preview: string | null;
}

const DESCRIPTION_MAX = 500;
const IVA_RATE = 0.16;

@Component({
    selector: 'app-invoicing-warehouse-new',
    templateUrl: './invoicing-warehouse-new.component.html',
    styleUrls: ['../../order/bill-service/bill-service.component.css', './invoicing-warehouse-new.component.css']
})
export class InvoicingWarehouseNewComponent implements OnInit, OnDestroy {
    readonly descriptionMax = DESCRIPTION_MAX;

    // --- 1 · Datos fiscales del cliente ---
    clients: WarehouseClient[] = [];
    selectedClientId: number | null = null;
    clientOpen = false;
    clientSearchText = '';

    // --- 2 · Servicios a facturar ---
    pendingServices: WarehousePendingService[] = [];
    serviceLines: ServiceLine[] = [];
    servicesOpen = false;
    loadingServices = false;
    serviceSearch = '';

    readonly units: Array<{ key: string; name: string }> = [
        { key: 'E48', name: 'Unidad de servicio' },
        { key: 'H87', name: 'Pieza' },
        { key: 'ACT', name: 'Actividad' },
        { key: 'HUR', name: 'Hora' },
        { key: 'DAY', name: 'Día' },
        { key: 'MON', name: 'Mes' }
    ];

    // --- 3 · Configuración CFDI ---
    readonly paymentForms: Array<{ key: string; name: string }> = [
        { key: '01', name: 'Efectivo' },
        { key: '02', name: 'Cheque nominativo' },
        { key: '03', name: 'Transferencia electrónica de fondos' },
        { key: '04', name: 'Tarjeta de crédito' },
        { key: '28', name: 'Tarjeta de débito' },
        { key: '99', name: 'Por definir' }
    ];
    selectedPaymentFormKey: string | null = null;
    paymentFormOpen = false;

    readonly paymentMethods: Array<{ key: string; name: string }> = [
        { key: 'PUE', name: 'Pago en una sola exhibición' },
        { key: 'PPD', name: 'Pago en parcialidades o diferido' }
    ];
    selectedPaymentMethodKey: string | null = null;
    paymentMethodOpen = false;

    // --- 4 · Concepto a facturar ---
    productServiceKeys: Array<{ key: string; name: string }> = [];
    selectedProductServiceKey: string | null = null;
    productServiceOpen = false;

    description = '';

    supplies: Supply[] = [];
    supplyLines: SupplyLine[] = [this.emptySupplyLine()];

    // Modal de alta de insumo.
    supplyModalOpen = false;
    newSupplyName = '';
    newSupplyCost: number | null = null;

    readonly taxObjectOptions: Array<{ key: string; name: string }> = [
        { key: '01', name: 'No objeto de impuesto' },
        { key: '02', name: 'Sí objeto de impuesto' }
    ];
    selectedTaxObjectKey: string | null = null;
    taxObjectOpen = false;

    // --- 5 · Comprobante de pago ---
    receipt: Receipt | null = null;

    isGenerating = false;

    private searchSub?: Subscription;

    constructor(
        private location: Location,
        private router: Router,
        private warehouseInvoiceService: WarehouseInvoiceService,
        private warehouseService: WarehouseService,
        private elementRef: ElementRef
    ) {}

    ngOnInit(): void {
        this.clients = this.warehouseInvoiceService.clients;
        this.supplies = this.warehouseInvoiceService.concepts.map(name => ({ name, cost: null }));

        // Las claves SAT del desplegable son las que ya usan los servicios de
        // almacén, para no ofrecer claves que la operación nunca factura.
        const catalog = this.warehouseInvoiceService.conceptCatalog;
        this.productServiceKeys = [...new Map(catalog.map(concept =>
            [concept.productServiceKey, { key: concept.productServiceKey, name: concept.name }])).values()];

        this.loadPendingServices();
    }

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
        this.releaseReceipt();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        // Con el modal abierto no se cierra nada: su fondo ya bloquea el resto.
        if (this.supplyModalOpen) return;
        if (!this.elementRef.nativeElement.contains(event.target)) this.closeAll();
    }

    private closeAll(except?: string): void {
        const menus = ['clientOpen', 'servicesOpen', 'paymentFormOpen', 'paymentMethodOpen',
            'productServiceOpen', 'taxObjectOpen'];
        menus.forEach(menu => { if (menu !== except) (this as any)[menu] = false; });
        this.supplyLines.forEach(line => { line.open = false; });
    }

    private round2(value: number): number {
        return Math.round(value * 100) / 100;
    }

    // ---------- 1 · Cliente ----------

    get selectedClient(): WarehouseClient | null {
        return this.clients.find(client => client.id === this.selectedClientId) ?? null;
    }

    get filteredClients(): WarehouseClient[] {
        const needle = this.clientSearchText.trim().toLowerCase();
        if (!needle) return this.clients;
        return this.clients.filter(client =>
            `${client.name} ${client.rfc} ${client.businessName}`.toLowerCase().includes(needle));
    }

    toggleClient(): void {
        const next = !this.clientOpen;
        this.closeAll('clientOpen');
        this.clientOpen = next;
    }

    selectClient(client: WarehouseClient): void {
        const changed = this.selectedClientId !== client.id;
        this.selectedClientId = client.id;
        this.clientOpen = false;
        // Un CFDI tiene un solo receptor: los servicios de otro cliente que ya
        // estuvieran asociados dejan de ser facturables aquí.
        if (changed) this.serviceLines = [];
        this.loadPendingServices();
    }

    // ---------- 2 · Servicios a facturar ----------

    private loadPendingServices(): void {
        this.loadingServices = true;
        this.searchSub?.unsubscribe();
        this.searchSub = this.warehouseInvoiceService.pendingServices(this.serviceSearch).subscribe(services => {
            this.pendingServices = this.selectedClientId
                ? services.filter(service => service.clientId === this.selectedClientId)
                : services;
            this.loadingServices = false;
        });
    }

    onServiceSearch(): void {
        this.loadPendingServices();
    }

    toggleServices(): void {
        const next = !this.servicesOpen;
        this.closeAll('servicesOpen');
        this.servicesOpen = next;
        if (next) this.loadPendingServices();
    }

    isServiceSelected(serviceId: number): boolean {
        return this.serviceLines.some(line => line.serviceId === serviceId);
    }

    /** Asociar un servicio lo trae ya capturado; se puede ajustar en la tabla. */
    toggleServiceSelection(service: WarehousePendingService): void {
        if (this.isServiceSelected(service.id)) {
            this.removeServiceLine(service.id);
            return;
        }

        // El primero fija el receptor de la factura.
        if (!this.selectedClientId) this.selectedClientId = service.clientId;

        this.serviceLines = [...this.serviceLines, {
            serviceId: service.id,
            folio: service.folio,
            warehouse: service.warehouse,
            concept: service.concept,
            description: service.concept,
            quantity: service.quantity,
            unitKey: service.unitKey,
            unitPrice: service.unitPrice,
            productServiceKey: service.productServiceKey,
            taxObjectKey: '02'
        }];
    }

    removeServiceLine(serviceId: number): void {
        this.serviceLines = this.serviceLines.filter(line => line.serviceId !== serviceId);
    }

    serviceAmount(line: ServiceLine): number {
        return this.round2(Number(line.quantity || 0) * Number(line.unitPrice || 0));
    }

    isServiceLineValid(line: ServiceLine): boolean {
        return !!(line.description.trim() && line.unitKey && line.productServiceKey && line.taxObjectKey)
            && Number(line.quantity) > 0 && Number(line.unitPrice) > 0;
    }

    // ---------- 3 · CFDI ----------

    get selectedPaymentForm() {
        return this.paymentForms.find(form => form.key === this.selectedPaymentFormKey) ?? null;
    }

    get selectedPaymentMethod() {
        return this.paymentMethods.find(method => method.key === this.selectedPaymentMethodKey) ?? null;
    }

    /** Regla del SAT: en PPD la forma de pago sólo puede ser 99 (Por definir). */
    get isPaymentFormLocked(): boolean {
        return this.selectedPaymentMethodKey === 'PPD';
    }

    togglePaymentForm(): void {
        if (this.isPaymentFormLocked) return;
        const next = !this.paymentFormOpen;
        this.closeAll('paymentFormOpen');
        this.paymentFormOpen = next;
    }

    selectPaymentForm(key: string): void {
        this.selectedPaymentFormKey = key;
        this.paymentFormOpen = false;
    }

    togglePaymentMethod(): void {
        const next = !this.paymentMethodOpen;
        this.closeAll('paymentMethodOpen');
        this.paymentMethodOpen = next;
    }

    selectPaymentMethod(key: string): void {
        this.selectedPaymentMethodKey = key;
        this.paymentMethodOpen = false;
        if (key === 'PPD') this.selectedPaymentFormKey = '99';
    }

    // ---------- 4 · Concepto ----------

    get selectedProductService() {
        return this.productServiceKeys.find(item => item.key === this.selectedProductServiceKey) ?? null;
    }

    toggleProductService(): void {
        const next = !this.productServiceOpen;
        this.closeAll('productServiceOpen');
        this.productServiceOpen = next;
    }

    selectProductService(key: string): void {
        this.selectedProductServiceKey = key;
        this.productServiceOpen = false;
    }

    get selectedTaxObject() {
        return this.taxObjectOptions.find(option => option.key === this.selectedTaxObjectKey) ?? null;
    }

    toggleTaxObject(): void {
        const next = !this.taxObjectOpen;
        this.closeAll('taxObjectOpen');
        this.taxObjectOpen = next;
    }

    selectTaxObject(key: string): void {
        this.selectedTaxObjectKey = key;
        this.taxObjectOpen = false;
    }

    private emptySupplyLine(): SupplyLine {
        return { supply: null, quantity: null, unitPrice: null, open: false };
    }

    toggleSupply(line: SupplyLine): void {
        const next = !line.open;
        this.closeAll();
        line.open = next;
    }

    selectSupply(line: SupplyLine, supply: Supply): void {
        line.supply = supply.name;
        line.open = false;
        // El costo capturado al darlo de alta sirve de punto de partida; la
        // línea se puede ajustar antes de facturar.
        if (supply.cost !== null && !line.unitPrice) line.unitPrice = supply.cost;
    }

    addSupplyLine(): void {
        this.supplyLines.push(this.emptySupplyLine());
    }

    removeSupplyLine(index: number): void {
        this.supplyLines.splice(index, 1);
        // Siempre queda una línea: sin ninguna no habría dónde capturar.
        if (!this.supplyLines.length) this.supplyLines.push(this.emptySupplyLine());
    }

    /** Líneas con algo capturado. Una fila en blanco no invalida el formulario. */
    get filledSupplyLines(): SupplyLine[] {
        return this.supplyLines.filter(line => line.supply || line.quantity || line.unitPrice);
    }

    isSupplyLineValid(line: SupplyLine): boolean {
        return !!line.supply && Number(line.quantity) > 0 && Number(line.unitPrice) > 0;
    }

    lineAmount(line: SupplyLine): number {
        return this.round2(Number(line.quantity || 0) * Number(line.unitPrice || 0));
    }

    // ---------- Modal de alta de insumo ----------

    openSupplyModal(): void {
        this.closeAll();
        this.newSupplyName = '';
        this.newSupplyCost = null;
        this.supplyModalOpen = true;
    }

    closeSupplyModal(): void {
        this.supplyModalOpen = false;
    }

    get canRegisterSupply(): boolean {
        return !!this.newSupplyName.trim() && Number(this.newSupplyCost) > 0;
    }

    /** Alta local de insumo: no hay catálogo de insumos en el backend, así que
     *  vive en esta pantalla y se pierde al salir. */
    registerSupply(): void {
        if (!this.canRegisterSupply) return;
        const name = this.newSupplyName.trim();
        const cost = Number(this.newSupplyCost);

        const existing = this.supplies.find(supply => supply.name.toLowerCase() === name.toLowerCase());
        const supply: Supply = existing ?? { name, cost };
        if (existing) {
            existing.cost = cost;
        } else {
            this.supplies = [...this.supplies, supply];
        }

        // Se coloca en la primera línea libre para no obligar a buscarlo.
        const target = this.supplyLines.find(line => !line.supply) ?? this.supplyLines[0];
        this.selectSupply(target, supply);
        this.closeSupplyModal();
    }

    // ---------- Totales ----------

    get subtotal(): number {
        const services = this.serviceLines.reduce((acc, line) => acc + this.serviceAmount(line), 0);
        const supplies = this.filledSupplyLines.reduce((acc, line) => acc + this.lineAmount(line), 0);
        return this.round2(services + supplies);
    }

    get iva(): number {
        return this.round2(this.subtotal * IVA_RATE);
    }

    get total(): number {
        return this.round2(this.subtotal + this.iva);
    }

    // ---------- 5 · Comprobante ----------

    onReceiptSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        this.releaseReceipt();
        const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
        this.receipt = {
            name: file.name,
            isPdf,
            preview: isPdf ? null : URL.createObjectURL(file)
        };
        // Permite volver a elegir el mismo archivo tras quitarlo.
        input.value = '';
    }

    removeReceipt(): void {
        this.releaseReceipt();
        this.receipt = null;
    }

    private releaseReceipt(): void {
        if (this.receipt?.preview) URL.revokeObjectURL(this.receipt.preview);
    }

    // ---------- Envío ----------

    get isFormValid(): boolean {
        const supplies = this.filledSupplyLines;
        if (!this.serviceLines.length && !supplies.length) return false;
        if (!this.selectedClientId || !this.selectedPaymentFormKey || !this.selectedPaymentMethodKey) return false;
        if (!this.serviceLines.every(line => this.isServiceLineValid(line))) return false;
        if (!supplies.every(line => this.isSupplyLineValid(line))) return false;

        // Sin servicios asociados el concepto lo define el paso 4, así que ahí
        // sí hacen falta clave, descripción e impuesto.
        if (!this.serviceLines.length) {
            return !!(this.selectedProductServiceKey && this.description.trim() && this.selectedTaxObjectKey);
        }
        return true;
    }

    onCancel(): void {
        this.goBack();
    }

    onSubmit(): void {
        if (!this.isFormValid || this.isGenerating) return;
        this.isGenerating = true;

        const lines = [
            ...this.serviceLines.map(line => ({
                concept: line.concept,
                productServiceKey: line.productServiceKey,
                warehouse: line.warehouse,
                quantity: Number(line.quantity),
                amount: this.serviceAmount(line)
            })),
            ...this.filledSupplyLines.map(line => ({
                concept: line.supply as string,
                productServiceKey: this.selectedProductServiceKey ?? '',
                warehouse: null as string | null,
                quantity: Number(line.quantity),
                amount: this.lineAmount(line)
            }))
        ];

        // El listado muestra un servicio de almacén por factura, así que la
        // consolidada se representa con la línea de mayor importe.
        const main = [...lines].sort((a, b) => b.amount - a.amount)[0];
        const quantity = lines.reduce((acc, line) => acc + line.quantity, 0);
        const amount = lines.reduce((acc, line) => acc + line.amount, 0);

        this.warehouseInvoiceService.add({
            clientId: Number(this.selectedClientId),
            warehouse: main.warehouse ?? this.warehouseService.current.name,
            concept: main.concept,
            productServiceKey: main.productServiceKey || (this.selectedProductServiceKey as string),
            quantity,
            unitPrice: quantity ? amount / quantity : 0,
            paymentMethod: this.selectedPaymentMethodKey as 'PUE' | 'PPD',
            paymentFormKey: this.selectedPaymentFormKey as string
        }).subscribe({
            next: invoice => {
                this.isGenerating = false;
                const consolidated = lines.length > 1
                    ? `<br>Consolida ${lines.length} conceptos.`
                    : '';
                Swal.fire({
                    title: 'Factura creada',
                    html: `Folio <b>${invoice.folio}</b> por <b>${this.formatCurrency(invoice.total)}</b>.${consolidated}`
                        + '<br><small>Sólo vive en memoria: al recargar vuelve el juego de prueba original.</small>',
                    icon: 'success',
                    confirmButtonText: 'Ver listado'
                }).then(() => this.router.navigate(['/invoicing/warehouse']));
            },
            error: () => {
                this.isGenerating = false;
                Swal.fire({ title: 'No se pudo crear', icon: 'error', confirmButtonText: 'Entendido' });
            }
        });
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
    }

    goBack(): void {
        if (window.history.length > 1) {
            this.location.back();
        } else {
            this.router.navigate(['/invoicing/warehouse']);
        }
    }
}
